import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import adminApi, { errMsg } from '../../api';
import { useAdminAuth } from '../../auth';
import { Card } from '../../components/Card';
import EmptyState from '../../components/EmptyState';
import PageHeader from '../../components/PageHeader';
import StatusPill from '../../components/StatusPill';
import { ConfirmDialog } from '../../components/Dialog';
import { ROLES } from '../../roles';

/**
 * System › Staff — owner only.
 *
 * Two changes from the old page. Deactivating someone now confirms first —
 * it was a click on what looked like a status badge, and it takes effect on
 * that person's very next request. And each role now says what it can
 * actually do, because "analyst / support / operator / owner" means nothing
 * to whoever is adding their first colleague.
 */
const ROLE_HELP = {
  analyst: 'Read-only. Sees every aggregate number, no individual user and no controls.',
  support: 'Read-only, plus lookup of one user\'s activity for answering a support ticket.',
  operator: 'Everything support can do, plus brand onboarding, product moderation, crawl triggers and flags.',
  owner: 'Everything, plus staff management and reading conversation transcripts.',
};

const input = 'rounded-lg border border-hairline px-3 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-raspberry-300';

export default function StaffPage() {
  const { admin } = useAdminAuth();
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [phone, setPhone] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState('analyst');
  const [creating, setCreating] = useState(false);
  const [pending, setPending] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get('/staff');
      setStaff(data.items || []);
    } catch (err) {
      toast.error(errMsg(err, 'Could not load staff'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await adminApi.post('/staff', { phone, name, role });
      setPhone(''); setName(''); setRole('analyst');
      await load();
      toast.success(`${name} added as ${role}`);
    } catch (err) {
      toast.error(errMsg(err, 'Could not create admin'));
    } finally {
      setCreating(false);
    }
  };

  const toggleActive = async () => {
    try {
      await adminApi.patch(`/staff/${pending.admin_id}`, { active: !pending.active });
      setPending(null);
      await load();
    } catch (err) {
      toast.error(errMsg(err, 'Could not update admin'));
      setPending(null);
    }
  };

  return (
    <div>
      <PageHeader
        title="Staff"
        hint="A new admin can sign in with their phone number as soon as they are added. A deactivation takes effect on their very next request."
      />

      <Card title="Add an admin" className="mb-6">
        <form onSubmit={create} className="flex flex-wrap gap-2">
          <input required placeholder="Phone (e.g. 9876543210)" value={phone}
            onChange={(e) => setPhone(e.target.value)} className={`min-w-[160px] flex-1 ${input}`} />
          <input required placeholder="Name" value={name}
            onChange={(e) => setName(e.target.value)} className={`min-w-[160px] flex-1 ${input}`} />
          <select value={role} onChange={(e) => setRole(e.target.value)} className={input}>
            {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
          <button type="submit" disabled={creating}
            className="rounded-lg bg-raspberry-600 px-4 py-2 text-sm font-medium text-white hover:bg-raspberry-700 disabled:opacity-50">
            {creating ? 'Adding…' : 'Add admin'}
          </button>
        </form>
        <p className="mt-2.5 text-xs leading-relaxed text-ink-secondary">
          <span className="font-medium capitalize text-ink">{role}:</span> {ROLE_HELP[role]}
        </p>
      </Card>

      {loading ? (
        <Card><p className="text-sm text-ink-muted">Loading…</p></Card>
      ) : staff.length === 0 ? (
        <EmptyState title="No staff accounts" />
      ) : (
        <div className="divide-y divide-hairline overflow-hidden rounded-xl border border-hairline bg-surface shadow-sm">
          {staff.map((a) => (
            <div key={a.admin_id} className="flex items-center justify-between gap-4 px-4 py-3.5">
              <div className="min-w-0">
                <div className="text-sm font-medium text-ink">
                  {a.name}
                  {a.admin_id === admin?.admin_id && (
                    <span className="ml-2 rounded-full bg-surface-page px-1.5 py-0.5 text-[10px] font-medium text-ink-muted">you</span>
                  )}
                </div>
                <div className="text-xs text-ink-secondary">
                  <span className="capitalize">{a.role}</span> · {a.phone}
                </div>
                <div className="mt-0.5 text-[11px] leading-snug text-ink-muted">{ROLE_HELP[a.role]}</div>
              </div>
              <div className="flex flex-shrink-0 items-center gap-3">
                <StatusPill tone={a.active ? 'good' : 'neutral'} label={a.active ? 'Active' : 'Deactivated'} />
                {/* No self-deactivation: locking yourself out of the only
                    owner-gated screen would require another owner to undo,
                    and there may not be one. */}
                {a.admin_id !== admin?.admin_id && (
                  <button
                    onClick={() => setPending(a)}
                    className="rounded-md border border-hairline px-2.5 py-1 text-[11px] font-medium text-ink-secondary hover:bg-surface-page hover:text-ink"
                  >
                    {a.active ? 'Deactivate' : 'Reactivate'}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <ConfirmDialog
        open={Boolean(pending)}
        tone={pending?.active ? 'critical' : 'default'}
        title={pending?.active ? `Deactivate ${pending?.name}?` : `Reactivate ${pending?.name}?`}
        body={pending?.active
          ? 'They lose access on their very next request — an open session does not keep working. Their past actions stay in the audit log.'
          : 'They will be able to sign in again with their phone number, with the same role they had.'}
        confirmLabel={pending?.active ? 'Deactivate' : 'Reactivate'}
        onConfirm={toggleActive}
        onClose={() => setPending(null)}
      />
    </div>
  );
}
